import { useDraggable, useDroppable } from "@dnd-kit/react";
import { AlertDialogTitle } from "@radix-ui/react-alert-dialog";
import { ChevronDown, ChevronRight, Edit2, Folder, FolderPlus, GripVertical, Plus, Trash2 } from "lucide-react";
import React, { useRef, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/NewButton";
import type { Document } from "@/hooks/useDocuments";
import { cleanFileName, getFileExtension, tempId } from "@/utils/file-utils";
import { BundleDocumentSelectorDialog } from "../BundleDocumentSelectorDialog";
import { useAllFileGuids, useBundleTree, useBundleTreeActions } from "../bundleTreeStore";
import { type FileNode, GroupType, getSubtreeDepth, sectionChildCounts } from "../bundleTypes";
import { InlineTextEditor } from "../InlineTextEditor";
import { RowActions } from "../RowActions";
import { useScrollIntoViewWhen } from "../useScrollIntoViewWhen";
import { EditEmailGroup } from "./EditEmailGroup";
import { EditFile } from "./EditFile";
import { GapDropZone, isNoOpDrop, nearbyCollisionDetector } from "./GapDropZone";

const MAX_GROUP_DEPTH = 3;

type EditSectionProps = {
  groupId: string;
  ancestorIds?: string[];
  depth?: number;
  scrollTo?: boolean;
  onScrollComplete?: () => void;
}

export const EditSection = ({groupId, ancestorIds = [], depth = 1, scrollTo, onScrollComplete}: EditSectionProps) => {
  const matterGuid = useBundleTree((s) => s.matterGuid);
  const config = useBundleTree((s) => s.config);
  const isExpanded = useBundleTree((s) => s.expandedIds.has(groupId));
  const {
    toggleExpand: onToggleExpand,
    updateGroup: onUpdateGroup,
    removeGroup: onRemoveGroup,
    addGroup: onAddGroup,
    addFiles: onAddFiles,
  } = useBundleTreeActions();
  const allFileGuids = useAllFileGuids();

  const group = config.groups[groupId];
  const childIds = group?.children || [];
  const { docCount, subCount, isEmpty } = sectionChildCounts(config, groupId);
  const canAddSubgroup = depth < MAX_GROUP_DEPTH;

  const { ref: sortRef, handleRef, isDragSource } = useDraggable({
    id: groupId,
    data: { type: 'group', groupId },
  });

  const [editingTitle, setEditingTitle] = useState(!!group.isNew);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const groupRef = useRef<HTMLDivElement | null>(null);

  useScrollIntoViewWhen(groupRef, group.isNew);
  useScrollIntoViewWhen(groupRef, scrollTo, onScrollComplete);

  const handleSubmitTitle = (text: string) => {
    const finalTitle = text.trim() || group.title;
    onUpdateGroup(groupId, { title: finalTitle, isNew: false });
    setEditingTitle(false);
  };

  const handleAddSubgroup = () => {
    const id = tempId();
    const count = childIds.filter(cid => !!config.groups[cid]).length;
    onAddGroup(id, `Subgroup ${count + 1}`, groupId, GroupType.SECTION);
    if (!isExpanded) onToggleExpand(groupId);
  };

  const handleAddDocuments = (docs: Document[]) => {
    const newFiles: FileNode[] = docs.map((doc) => ({
      fileId: tempId(),
      fileReferenceGuid: doc.id,
      fileName: cleanFileName(doc.fileName),
      fileExtension: getFileExtension(doc.fileName),
      customName: null,
      pageRange: null,
      isInsert: false,
      parentGroupId: groupId,
    }));
    onAddFiles(groupId, newFiles);
    setShowDocPicker(false);
    if (!isExpanded) onToggleExpand(groupId);
  };

  const makeAcceptFn = (insertBeforeId?: string) => (source: any) => {
    if (!source?.data) return false;
    if (source.data.type === 'file') {
      const sourceId = source.data.bundleFileId as string;
      // Email attachments are locked inside their email group
      const sourceParentId = source.data.groupId as string | undefined;
      const sourceParent = sourceParentId ? config.groups[sourceParentId] : null;
      if (sourceParent?.groupType === GroupType.EMAIL) return false;
      return !isNoOpDrop(sourceId, childIds, insertBeforeId);
    }
    if (source.data.type === 'group') {
      const sourceId = source.data.groupId as string;
      if (sourceId === groupId) return false;
      if (ancestorIds.includes(sourceId)) return false;
      // Volumes cannot be nested inside other groups
      if (config.groups[sourceId]?.groupType === GroupType.VOLUME) return false;
      if (isNoOpDrop(sourceId, childIds, insertBeforeId)) return false;
      // Check if dropping this group here would exceed max depth
      const sourceSubtreeDepth = getSubtreeDepth(config, sourceId);
      return depth + 1 + sourceSubtreeDepth <= MAX_GROUP_DEPTH;
    }
    return false;
  };

  const childAncestorIds = [...ancestorIds, groupId];

  const isCollapsedDroppable = !isExpanded && !isDragSource;
  const { ref: collapsedDropRef, isDropTarget: isCollapsedDropTarget } = useDroppable({
    id: `collapsed-drop-${groupId}`,
    data: { type: 'gap', parentGroupId: groupId, insertBeforeId: undefined },
    collisionDetector: nearbyCollisionDetector,
    accept: makeAcceptFn(),
    disabled: !isCollapsedDroppable,
  });

  return (
    <div
      ref={(node) => {
        sortRef(node); groupRef.current = node;
        if (isCollapsedDroppable) collapsedDropRef(node);
        else collapsedDropRef(null);
      }}
      className={`relative rounded-lg bg-white transition-colors ${isDragSource ? 'opacity-30' : ''} ${isCollapsedDropTarget ? 'ring-2 ring-crimson-500' : 'border'}`}
    >
      <div className={`flex items-center gap-2 px-3 py-2 bg-gray-50 group/header ${isDragSource ? 'rounded-lg' : isExpanded ? 'rounded-t-lg border-b' : 'rounded-lg'}`}>
        <GripVertical ref={handleRef} className="h-4 w-4 text-gray-300 shrink-0 cursor-grab outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:rounded-sm" />
        <Button
          icon={isExpanded ? ChevronDown : ChevronRight}
          variant="bare"
          size="icon-md"
          onClick={() => onToggleExpand(groupId)}
          className="text-gray-500 hover:text-gray-700"
        />

        {editingTitle ? (
          <InlineTextEditor
            initialValue={group.isNew ? '' : group.title}
            onSubmit={handleSubmitTitle}
            onCancel={() => setEditingTitle(false)}
            placeholder="Group name..."
            inputClassName="h-7 text-sm flex-1"
          />
        ) : (
          <>
            <Folder className="h-4 w-4 text-gray-400 shrink-0" />
            <span
              className="text-sm font-medium flex-1 cursor-pointer"
              onDoubleClick={() => setEditingTitle(true)}
            >
              {group.title}
              <span className="text-xs text-gray-400 font-normal ml-2">
                {docCount} doc{docCount !== 1 ? 's' : ''}
                {subCount > 0 && ` · ${subCount} section${subCount !== 1 ? 's' : ''}`}
              </span>
            </span>
          </>
        )}

        <RowActions
          actions={[
            { icon: Edit2, label: 'Rename group', onClick: () => setEditingTitle(true), visible: !editingTitle },
            { icon: FolderPlus, label: 'Add subgroup', onClick: handleAddSubgroup, visible: canAddSubgroup, className: 'text-gray-400 hover:text-blue-600' },
            { icon: Plus, label: 'Add documents', onClick: () => setShowDocPicker(true), className: 'text-gray-400 hover:text-green-600' },
            { icon: Trash2, label: 'Delete section', onClick: () => (isEmpty ? onRemoveGroup(groupId) : setShowDeleteConfirm(true)), danger: true },
          ]}
        />
      </div>

      {!isDragSource && isExpanded && (isEmpty ? (
        <GapDropZone
          id={`gap-${groupId}-end`}
          parentGroupId={groupId}
          accept={makeAcceptFn()}
          variant="empty-section"
        />
      ) : (
        <div className="px-5 py-3 space-y-1">
          {childIds.map((childId) => {
            const file = config.files[childId];
            return (
              <React.Fragment key={childId}>
                <GapDropZone
                  id={`gap-${groupId}-before-${childId}`}
                  parentGroupId={groupId}
                  insertBeforeId={childId}
                  accept={makeAcceptFn(childId)}
                />
                {file ? (
                  <EditFile file={file} />
                ) : config.groups[childId]?.groupType === GroupType.EMAIL ? (
                  <EditEmailGroup groupId={childId} />
                ) : (
                  <EditSection
                    groupId={childId}
                    ancestorIds={childAncestorIds}
                    depth={depth + 1}
                  />
                )}
              </React.Fragment>
            );
          })}
          <GapDropZone
            id={`gap-${groupId}-end`}
            parentGroupId={groupId}
            accept={makeAcceptFn()}
          />
        </div>
      ))}

      <BundleDocumentSelectorDialog
        open={showDocPicker}
        onOpenChange={setShowDocPicker}
        matterGuid={matterGuid}
        alreadyInBundleGuids={allFileGuids}
        onAddDocuments={handleAddDocuments}
        title={`Add Documents to "${group.title}"`}
      />
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{group.title}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This section contains {docCount} document{docCount !== 1 ? 's' : ''}{subCount > 0 ? ` and ${subCount} subsection${subCount !== 1 ? 's' : ''}` : ''}. Deleting it will remove all its contents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onRemoveGroup(groupId)} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

